import { getSupportedFieldConfiguration } from '@atlassian/ui-modifications-core/controllers';
import type { FieldForInitialize } from '@atlassian/ui-modifications-core/types';
import { REQUEST_CREATE_VIEW_TYPE } from '../../constants';
import type { FieldState } from 'state/persisted/request-create/types';
import type { RequestCreateViewFieldConfiguration } from './index';

interface GetValueByInitializeFieldPropertyMappingPayload<DEFAULT_VALUE> {
	field: FieldState;
	fieldType: string;
	property: keyof FieldForInitialize;
	defaultValue?: DEFAULT_VALUE;
}

export const getValueByInitializeFieldPropertyMapping = <RESULT, DEFAULT_VALUE = undefined>({
	field,
	fieldType,
	property,
	defaultValue = undefined,
}: GetValueByInitializeFieldPropertyMappingPayload<DEFAULT_VALUE>) => {
	const mappedProperty =
		getSupportedFieldConfiguration<RequestCreateViewFieldConfiguration>(
			REQUEST_CREATE_VIEW_TYPE,
			fieldType,
		)?.initializeFieldPropertyMapping?.[property] ?? property;

	return (field?.[mappedProperty as keyof FieldState] as RESULT) ?? defaultValue;
};

/**
 * When a field is pre-populated via a URL query parameter, the raw parsed value may need to
 * be converted into the shape that UIM's internalToPublicTransformers expect. This utility
 * delegates to the field configuration's `transformUrlParamValue` if one is defined, otherwise
 * returns the raw value as-is.
 */
export const transformUrlParamValueForField = (
	urlParamValue: unknown,
	field: FieldState,
	fieldType: string,
): unknown => {
	const transformer = getSupportedFieldConfiguration<RequestCreateViewFieldConfiguration>(
		REQUEST_CREATE_VIEW_TYPE,
		fieldType,
	)?.transformUrlParamValue;

	return transformer ? transformer(urlParamValue, field) : urlParamValue;
};
